import React from 'react';
import useTranslation from '../../../hooks/useTranslation';

interface AddressFormFieldsProps {
  mainAddress: string;
  detailAddress: string;
  onMainAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDetailAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isEditMode: boolean;
  isMainAddressReadOnly?: boolean;
  touched: {
    mainAddress: boolean;
    detailAddress: boolean;
  };
  validation: {
    isMainAddressValid: boolean;
    mainAddressMessage: string | null;
    isDetailAddressValid: boolean;
  };
  currentLocation?: { lat: number; lng: number } | null;
  isGeocoding?: boolean;
  locationError?: string | null;
}

const AddressFormFields: React.FC<AddressFormFieldsProps> = ({
  mainAddress,
  detailAddress,
  onMainAddressChange,
  onDetailAddressChange,
  isEditMode,
  isMainAddressReadOnly = false,
  touched,
  validation,
  currentLocation,
  isGeocoding = false,
  locationError
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Main Address Section */}
      <div className="mb-5">
        <label htmlFor="mainAddress" className="text-base font-medium text-gray-700 flex items-center mb-2">
          {t('address.main')}
          <span className="text-red-500 ml-1">*</span>
          {isEditMode && currentLocation && !isGeocoding && mainAddress.length >= 8 && (
            <span className="ml-2 flex items-center text-green-600 text-sm">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('address.geocoding.found')}
            </span>
          )}
          {(!isEditMode || isMainAddressReadOnly) && (
            <svg className="w-4 h-4 ml-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          )}
        </label>
        
        <input
          type="text"
          id="mainAddress"
          value={mainAddress}
          onChange={onMainAddressChange}
          readOnly={!isEditMode || isMainAddressReadOnly}
          className={`w-full border ${
            touched.mainAddress &&
            !validation.isMainAddressValid &&
            !mainAddress.includes('Latitude:')
              ? 'border-red-500 focus:ring-red-200'
              : isEditMode && currentLocation && !isGeocoding && mainAddress.length >= 8
              ? 'border-green-500 focus:ring-green-200'
              : isEditMode && !isMainAddressReadOnly
              ? 'border-gray-300 focus:ring-blue-200'
              : 'border-gray-200 focus:ring-gray-100'
          } ${
            isEditMode && !isMainAddressReadOnly
              ? 'bg-white text-gray-800 cursor-text' 
              : 'bg-gray-50 text-gray-400 cursor-default'
          } rounded-lg p-3 focus:outline-none ${
            isEditMode && !isMainAddressReadOnly ? 'focus:ring-2' : 'focus:ring-0'
          } transition-all duration-200`}
          placeholder={isEditMode && !isMainAddressReadOnly ? "Enter your address (e.g., 123 Main St, Seoul)" : ""}
        />
        
        {touched.mainAddress && !validation.isMainAddressValid && !mainAddress.includes('Latitude:') && (
          <p className="text-red-500 text-sm mt-1">{validation.mainAddressMessage}</p>
        )}
        
        {locationError && (
          <p className="text-amber-600 text-sm mt-1 bg-amber-50 p-2 rounded border border-amber-200">
            {locationError}
          </p>
        )}
      </div>
      
      {/* Detail Address Section */}
      <div className="mb-5">
        <label htmlFor="detailAddress" className="text-base font-medium text-gray-700 flex items-center mb-2">
          {t('address.detail')}
          <span className="text-red-500 ml-1">*</span>
          {!isEditMode && (
            <svg className="w-4 h-4 ml-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          )}
        </label>
        
        <input
          type="text"
          id="detailAddress"
          value={detailAddress}
          onChange={onDetailAddressChange}
          readOnly={!isEditMode}
          className={`w-full border ${
            touched.detailAddress && !validation.isDetailAddressValid
              ? 'border-red-500 focus:ring-red-200'
              : isEditMode
              ? 'border-gray-300 focus:ring-blue-200'
              : 'border-gray-200 focus:ring-gray-100'
          } ${
            isEditMode 
              ? 'bg-white text-gray-800 cursor-text' 
              : 'bg-gray-50 text-gray-400 cursor-default'
          } rounded-lg p-3 focus:outline-none ${
            isEditMode ? 'focus:ring-2' : 'focus:ring-0'
          } transition-all duration-200`}
          placeholder={isEditMode ? "Enter apartment number, building name, floor, etc." : ""}
        />
        
        {touched.detailAddress && !validation.isDetailAddressValid && (
          <p className="text-red-500 text-sm mt-1">{t('address.detailRequired')}</p>
        )}
      </div>
    </>
  );
};

export default AddressFormFields; 